import React, { ReactNode, useCallback, useRef } from 'react';
import { Box, Grid, Stack, GridProps, Typography, Theme, useMediaQuery } from '@razz-team/ui-web';
import { useEventListener } from 'usehooks-ts';

export interface MainLayoutProps extends Omit<
  GridProps,
  'title' | 'container' | 'item' | 'spacing'
> {
  title?: ReactNode;
  subtitle?: ReactNode;
  compact?: boolean;
  actions?: React.ReactNode;
  breadcrumbs?: boolean;
  onScrollRatioChange?: (ratio: number) => void;
}

export function useMainLayoutStyles() {
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('md'));
  const padding = isMobile ? 2 : 8;
  const spacing = isMobile ? 2 : 4;

  return { isMobile, padding, spacing };
}

export function MainLayout({
  title,
  subtitle,
  actions,
  compact,
  children,
  onScrollRatioChange,
  ...props
}: MainLayoutProps) {
  let titleElement = title ? <Typography variant="h2">{title}</Typography> : null;
  if (subtitle) {
    titleElement = (
      <Stack direction="column" spacing={1}>
        {titleElement}
        <Typography variant="subtitle2">{subtitle}</Typography>
      </Stack>
    );
  }
  const { isMobile, padding, spacing } = useMainLayoutStyles();

  const ref = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(
    (element: HTMLElement) => {
      if (onScrollRatioChange) {
        const { scrollTop, scrollHeight, clientHeight } = element;
        const scrollableHeight = scrollHeight - clientHeight;

        if (scrollableHeight > 0) {
          onScrollRatioChange(scrollTop / scrollableHeight);
        }
      }
    },
    [onScrollRatioChange],
  );

  useEventListener('scroll', () => handleScroll(document.documentElement), undefined, {
    passive: true,
  });

  return (
    <Box
      sx={{ padding, overflowY: 'auto', height: '100%' }}
      ref={ref}
      onScroll={(e) => handleScroll(e.currentTarget)}
    >
      <Grid container spacing={spacing} {...props}>
        <Grid size={12}>
          {actions ? (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              display="flex"
              width="100%"
            >
              {titleElement}
              {actions}
            </Stack>
          ) : (
            titleElement
          )}
        </Grid>
        {compact && !isMobile ? (
          <Grid width="100%" maxWidth={750} container spacing={spacing}>
            {children}
          </Grid>
        ) : (
          children
        )}
      </Grid>
    </Box>
  );
}
